import { useEffect, useState, useCallback, useRef } from "react";
import axios from "axios";
import {
  CheckCircle2, MapPin, Clock, RefreshCw, AlertCircle, Train,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { API } from "@/App";

const POLL_INTERVAL_MS = 60_000;

const formatIst = (iso) => {
  if (!iso) return null;
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return null;
    return d.toLocaleTimeString("en-IN", {
      timeZone: "Asia/Kolkata",
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    }) + " IST";
  } catch {
    return null;
  }
};

const delayLabel = (delay) => {
  if (delay == null) return "—";
  if (delay <= 0) return "On time";
  return `+${delay} min`;
};

const delayTone = (delay) => {
  if (delay == null) return "text-zinc-400";
  if (delay <= 0) return "text-emerald-400";
  if (delay <= 15) return "text-amber-400";
  return "text-rose-400";
};

const stationRowBg = (s) => {
  if (s.is_current) return "bg-sky-500/10 border-l-4 border-sky-500";
  if (s.is_passed) return "opacity-60";
  return "";
};

const stationIcon = (s) => {
  if (s.is_passed && !s.is_current) {
    return <CheckCircle2 className="w-4 h-4 text-emerald-400 shrink-0" />;
  }
  if (s.is_current) {
    return (
      <span className="relative shrink-0 w-4 h-4 inline-flex items-center justify-center">
        <span className="absolute w-4 h-4 rounded-full bg-sky-500/40 animate-ping" />
        <MapPin className="w-4 h-4 text-sky-400 relative" />
      </span>
    );
  }
  return <span className="shrink-0 w-4 h-4 inline-flex items-center justify-center">
    <span className="w-2 h-2 rounded-full bg-zinc-600" />
  </span>;
};

const LiveTrainStatus = ({ pnrId, userId, onClose }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const intervalRef = useRef(null);

  const fetchStatus = useCallback(async () => {
    if (!pnrId || !userId) return;
    try {
      const res = await axios.get(
        `${API}/pnr/${pnrId}/live-status?user_id=${userId}`
      );
      setData(res.data);
      setError(null);
    } catch (e) {
      const msg = e?.response?.status === 404
        ? "PNR not found."
        : "Could not load live status.";
      setError(msg);
    } finally {
      setLoading(false);
    }
  }, [pnrId, userId]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    fetchStatus();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pnrId, userId]);

  // Only poll when in active window; otherwise a single fetch is enough.
  useEffect(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    if (data?.in_active_window) {
      intervalRef.current = setInterval(fetchStatus, POLL_INTERVAL_MS);
    }
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [data?.in_active_window, fetchStatus]);

  const handleManualRefresh = () => {
    setLoading(true);
    fetchStatus();
  };

  if (loading && !data) {
    return (
      <div className="space-y-3" data-testid="live-train-status-loading">
        <Skeleton className="h-16 bg-zinc-800/40" />
        <Skeleton className="h-12 bg-zinc-800/40" />
        <Skeleton className="h-32 bg-zinc-800/40" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center gap-3 py-6" data-testid="live-train-status-error">
        <AlertCircle className="w-8 h-8 text-rose-400" />
        <p className="text-sm text-zinc-300">{error}</p>
        <Button
          size="sm"
          onClick={handleManualRefresh}
          className="bg-indigo-500 hover:bg-indigo-400 text-white"
        >
          <RefreshCw className="w-4 h-4 mr-2" />
          Retry
        </Button>
      </div>
    );
  }

  if (!data) return null;

  // Out-of-window: minimal static view.
  if (!data.in_active_window) {
    return (
      <div className="space-y-4" data-testid="live-train-status-inactive">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-zinc-800 flex items-center justify-center">
            <Train className="w-5 h-5 text-zinc-400" />
          </div>
          <div>
            <div className="text-base font-bold text-zinc-50">
              {data.train_number} {data.train_name && `— ${data.train_name}`}
            </div>
            <div className="text-xs text-zinc-400">
              📅 {data.journey_date} • {data.departure_time} IST
            </div>
          </div>
        </div>
        <div className="rounded-lg bg-zinc-800/50 border border-zinc-700 p-4">
          <p className="text-sm text-zinc-300">
            Tracking will start 6 hours before departure
            ({data.departure_time} on {data.journey_date}).
          </p>
        </div>
      </div>
    );
  }

  const segment = data.stations_segment || [];
  const lastUpdated = formatIst(data.fetched_at);

  return (
    <div className="space-y-4" data-testid="live-train-status-active">
      {/* Header */}
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-3 min-w-0">
          <div className="w-10 h-10 rounded-lg bg-indigo-500/20 flex items-center justify-center shrink-0">
            <Train className="w-5 h-5 text-indigo-400" />
          </div>
          <div className="min-w-0">
            <div className="text-base font-bold text-zinc-50 truncate">
              {data.train_number} {data.train_name && `— ${data.train_name}`}
            </div>
            <div className="text-xs text-zinc-400">
              📅 {data.journey_date} • {data.departure_time} IST
            </div>
          </div>
        </div>
        <Button
          size="sm"
          variant="ghost"
          onClick={handleManualRefresh}
          className="text-zinc-400 hover:text-white shrink-0"
          aria-label="Refresh"
        >
          <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
        </Button>
      </div>

      {/* Status card */}
      <div className="rounded-lg bg-sky-500/10 border border-sky-500/30 p-4">
        <div className="flex items-start justify-between gap-3 mb-2">
          <div className="text-sm font-medium text-zinc-50 leading-snug">
            {data.current_status || "Status unavailable"}
          </div>
          <span
            className={`shrink-0 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
              data.is_on_time
                ? "bg-emerald-500/20 text-emerald-300"
                : "bg-rose-500/20 text-rose-300"
            }`}
          >
            {data.is_on_time ? "✓ On time" : `↗ ${delayLabel(data.delay_mins)}`}
          </span>
        </div>
        {data.next_station_name && (
          <div className="text-xs text-zinc-400 flex items-center gap-1.5 mt-2">
            <MapPin className="w-3.5 h-3.5" />
            <span>
              Next: <span className="text-zinc-200">{data.next_station_name}</span>
              {data.next_station_code ? ` (${data.next_station_code})` : ""}
              {data.platform_number ? ` • Platform ${data.platform_number}` : ""}
            </span>
          </div>
        )}
      </div>

      {/* Segment timeline */}
      {segment.length > 0 ? (
        <div className="space-y-1" data-testid="live-train-status-segment">
          {segment.map((s) => (
            <div
              key={s.code}
              className={`flex items-start gap-3 px-3 py-2 rounded ${stationRowBg(s)}`}
            >
              <div className="pt-0.5">{stationIcon(s)}</div>
              <div className="flex-1 min-w-0">
                <div className="flex items-baseline gap-2 flex-wrap">
                  <span className="text-sm font-medium text-zinc-100">
                    {s.code}
                  </span>
                  <span className="text-xs text-zinc-400 truncate">{s.name}</span>
                </div>
                <div className="text-xs text-zinc-500 mt-0.5 flex items-center gap-2 flex-wrap">
                  <span>
                    {s.scheduled_arr || "--"}
                    {s.actual_arr && s.actual_arr !== "--" ? (
                      <span className={`ml-1 ${delayTone(s.delay_mins)}`}>
                        → {s.actual_arr}
                      </span>
                    ) : null}
                  </span>
                  {s.platform ? <span>• Plat. {s.platform}</span> : null}
                  {s.halt_min > 0 ? <span>🚏 {s.halt_min}m</span> : null}
                </div>
              </div>
              <div className={`text-xs font-medium ${delayTone(s.delay_mins)} shrink-0`}>
                {s.is_passed && !s.actual_arr
                  ? ""
                  : s.delay_mins > 0
                    ? `+${s.delay_mins}m`
                    : "✓"}
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="rounded-lg bg-zinc-800/50 border border-zinc-700 p-4">
          <p className="text-sm text-zinc-300">
            Live tracking unavailable for this journey right now.
          </p>
        </div>
      )}

      {/* Footer */}
      {lastUpdated && (
        <div className="text-xs text-zinc-500 text-center flex items-center justify-center gap-1.5">
          <Clock className="w-3 h-3" />
          Last updated {lastUpdated}
          {data.cached ? " (cached)" : ""}
        </div>
      )}
    </div>
  );
};

export default LiveTrainStatus;
